import { enumMembers, type ApiSchema } from "../apiSchema";
import { readStringList } from "../content/contentDecoders";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";

/**
 * Groups as permission sets (accounts.md "Groups as permission sets", GS-1 to GS-6): a scope's
 * ordered list of groups, its people, and one person's access panel, as the server answers
 * them. Nothing here decides who may do what; `manageable`, `givable` and the panel's lines
 * are the server's answers. Routes: `server/src/dmw/api/routes/accounts/access/`.
 */

export type Permission = ApiSchema<"Permission">;
export type Override = ApiSchema<"Override">;
export type SystemGroup = ApiSchema<"SystemGroup">;
export type Block = ApiSchema<"Block">;
export type AllowedBy = ApiSchema<"AllowedBy">;

export const PERMISSIONS = enumMembers<Permission>({
  view: true,
  propose: true,
  curate: true,
  release: true,
  server_keys: true,
  manage_people: true,
  change_rules: true,
  delete_project: true,
  create_projects: true,
  manage_accounts: true,
  manage_server_keys: true,
  change_installation: true,
  restore_projects: true,
});

const OVERRIDES = enumMembers<Override>({ on: true, off: true });

export const SYSTEM_GROUPS = enumMembers<SystemGroup>({
  administrators: true,
  everyone: true,
  project_administrators: true,
  curators: true,
  contributors: true,
  readers: true,
});

export const BLOCKS = enumMembers<Block>({ project_not_active: true, not_deletable: true, dmw_core: true });

export const ALLOWED_BY = enumMembers<AllowedBy>({ every_signed_in_person: true, administrators: true, item_curator: true });

/** One thing a group lets its people do: a permission, and for Curate one item kind. */
export interface Grant {
  readonly permission: Permission;
  /** The item kind for `curate`; `null` for every other permission. */
  readonly itemKind: string | null;
}

export interface GrantOverride extends Grant {
  readonly override: Override;
}

/** A group as the list names it, for names and paths ("Historians › Student assistants"). */
export interface NamedGroup {
  readonly groupId: string;
  readonly name: string;
  readonly parentId: string | null;
  /** `null` for an installation group. */
  readonly projectId: string | null;
}

export interface ListedGroup extends NamedGroup {
  readonly system: SystemGroup | null;
  /** The group's own set; for an installation group in a project, the installation's. */
  readonly grants: readonly Grant[];
  /** In a project, what it switched on or off for an included installation group. */
  readonly overrides: readonly GrantOverride[];
  /** What the group gives in this scope. */
  readonly effective: readonly Grant[];
  /** What can't be switched off here: a project's Administrators. */
  readonly fixed: readonly Grant[];
  /** The people put into it themselves. */
  readonly memberIds: readonly string[];
  /** Everyone it reaches, those of the groups inside it too; empty for Everyone. */
  readonly peopleIds: readonly string[];
  /** The viewer may change it, its set and its members. */
  readonly manageable: boolean;
}

export interface ItemCuration {
  readonly itemKind: string;
  readonly itemId: string;
}

export interface ListedPerson {
  readonly userId: string;
  /** The groups of the list that reach them, highest first. */
  readonly groupIds: readonly string[];
  readonly itemCurations: readonly ItemCuration[];
  /** The viewer may take them out of groups here. */
  readonly manageable: boolean;
  /** For each listed group that reaches them through a group inside it: the group ids from it down to their own (GR-2). */
  readonly paths: readonly (readonly string[])[];
}

export interface AccessPerson {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  /** False: the account can't sign in, so it may do nothing. */
  readonly active: boolean;
}

/** An account to put into a group (F15): its name and a masked email, never the address itself. */
export interface AccountSuggestion {
  readonly userId: string;
  readonly displayName: string;
  readonly emailHint: string;
}

/** One scope's list: the installation's (`projectId` null) or a project's. */
export interface AccessList {
  readonly projectId: string | null;
  /** Sent back with a new order. */
  readonly revision: number;
  /** First on top. */
  readonly groups: readonly ListedGroup[];
  readonly people: readonly ListedPerson[];
  /** Installation groups the project could include (for those who manage it). */
  readonly includableGroupIds: readonly string[];
  /** What the viewer may switch on here. */
  readonly givable: ReadonlySet<Permission>;
  /** Every kind Curate can name. */
  readonly itemKinds: readonly string[];
  readonly namedGroups: ReadonlyMap<string, NamedGroup>;
  readonly namedPeople: ReadonlyMap<string, AccessPerson>;
}

export interface Refusal {
  /** What stops it whatever anyone holds. */
  readonly block: Block | null;
  readonly missing: Grant | null;
}

export interface GrantSource {
  readonly groupId: string;
  /** From the giving group down to the person's own group. */
  readonly path: readonly string[];
  /** Switched on by this project for an included group. */
  readonly here: boolean;
}

/** One permission in the access panel, Curate once per item kind. */
export interface PanelLine {
  readonly grant: Grant;
  readonly allowed: boolean;
  /** A source that is none of their groups. */
  readonly allowedBy: AllowedBy | null;
  readonly refusal: Refusal | null;
  readonly sources: readonly GrantSource[];
  /** Groups here that give it, for a line they lack. */
  readonly withIt: readonly string[];
}

export interface PersonPanel {
  readonly person: AccessPerson;
  readonly lines: readonly PanelLine[];
  /** Who may change this person's access here, by name (Sol critique C12); `null` for an account gone since. */
  readonly managers: readonly (string | null)[];
  readonly namedGroups: ReadonlyMap<string, NamedGroup>;
}

export function decodeAccessList(body: unknown): AccessList {
  const list = readObject(body, "access list");
  return {
    projectId: readNullableString(list, "project_id"),
    revision: readInteger(list, "revision"),
    groups: readArray(list["groups"], "groups", (item) => decodeListedGroup(readObject(item, "group"))),
    people: readArray(list["people"], "people", (item) => decodeListedPerson(readObject(item, "person"))),
    includableGroupIds: readStringList(list, "includable_group_ids"),
    givable: new Set(readArray(list["givable"], "givable", (permission) => readOneOf({ permission }, "permission", PERMISSIONS))),
    itemKinds: readStringList(list, "item_kinds"),
    namedGroups: decodeNamedGroups(list),
    namedPeople: new Map(readArray(list["named_people"], "named people", (item) => {
      const person = decodeAccessPerson(readObject(item, "person"));
      return [person.userId, person] as const;
    })),
  };
}

export function decodePersonPanel(body: unknown): PersonPanel {
  const panel = readObject(body, "person panel");
  return {
    person: decodeAccessPerson(readObject(panel["person"], "person")),
    lines: readArray(panel["lines"], "lines", (item) => decodeLine(readObject(item, "line"))),
    managers: readArray(panel["managers"], "managers", (item) => readNullableString(readObject(item, "manager"), "display_name")),
    namedGroups: decodeNamedGroups(panel),
  };
}

/** The body of a grant the client sends. */
export function grantBody(grant: Grant): ApiSchema<"GrantChoiceBody"> {
  return { permission: grant.permission, item_kind: grant.itemKind };
}

function decodeListedGroup(group: JsonObject): ListedGroup {
  const system = readNullableString(group, "system");
  return {
    ...decodeNamedGroup(group),
    system: system === null ? null : readOneOf(group, "system", SYSTEM_GROUPS),
    grants: decodeGrants(group, "grants"),
    overrides: readArray(group["overrides"], "overrides", (item) => {
      const override = readObject(item, "override");
      return { ...decodeGrant(override), override: readOneOf(override, "override", OVERRIDES) };
    }),
    effective: decodeGrants(group, "effective"),
    fixed: decodeGrants(group, "fixed"),
    memberIds: readStringList(group, "member_ids"),
    peopleIds: readStringList(group, "people_ids"),
    manageable: readBoolean(group, "manageable"),
  };
}

function decodeListedPerson(person: JsonObject): ListedPerson {
  return {
    userId: readString(person, "user_id"),
    groupIds: readStringList(person, "group_ids"),
    itemCurations: readArray(person["item_curations"], "item curations", (item) => {
      const curation = readObject(item, "item curation");
      return { itemKind: readString(curation, "item_kind"), itemId: readString(curation, "item_id") };
    }),
    manageable: readBoolean(person, "manageable"),
    paths: readArray(person["paths"], "paths", (path) => readArray(path, "path", (groupId) => readString({ groupId }, "groupId"))),
  };
}

function decodeLine(line: JsonObject): PanelLine {
  const allowedBy = readNullableString(line, "allowed_by");
  const refusal = line["refusal"];
  return {
    grant: decodeGrant(readObject(line["grant"], "grant")),
    allowed: readBoolean(line, "allowed"),
    allowedBy: allowedBy === null ? null : readOneOf(line, "allowed_by", ALLOWED_BY),
    refusal: refusal === null ? null : decodeRefusal(readObject(refusal, "refusal")),
    sources: readArray(line["sources"], "sources", (item) => {
      const source = readObject(item, "source");
      return { groupId: readString(source, "group_id"), path: readStringList(source, "path"), here: readBoolean(source, "here") };
    }),
    withIt: readStringList(line, "with_it"),
  };
}

function decodeRefusal(refusal: JsonObject): Refusal {
  const block = readNullableString(refusal, "block");
  const missing = refusal["missing"];
  return {
    block: block === null ? null : readOneOf(refusal, "block", BLOCKS),
    missing: missing === null ? null : decodeGrant(readObject(missing, "missing grant")),
  };
}

function decodeGrants(object: JsonObject, member: string): readonly Grant[] {
  return readArray(object[member], member, (item) => decodeGrant(readObject(item, "grant")));
}

function decodeGrant(grant: JsonObject): Grant {
  return { permission: readOneOf(grant, "permission", PERMISSIONS), itemKind: readNullableString(grant, "item_kind") };
}

function decodeNamedGroups(object: JsonObject): ReadonlyMap<string, NamedGroup> {
  return new Map(readArray(object["named_groups"], "named groups", (item) => {
    const group = decodeNamedGroup(readObject(item, "group"));
    return [group.groupId, group] as const;
  }));
}

function decodeNamedGroup(group: JsonObject): NamedGroup {
  return {
    groupId: readString(group, "group_id"),
    name: readString(group, "name"),
    parentId: readNullableString(group, "parent_id"),
    projectId: readNullableString(group, "project_id"),
  };
}

/** A group every new project starts with, and what it allows (GS-5, `StarterGroupBody`). */
export interface StarterGroup {
  readonly system: SystemGroup;
  readonly grants: readonly Grant[];
}

export function decodeStarterGroups(body: unknown): readonly StarterGroup[] {
  return readArray(body, "starter groups", (item) => {
    const group = readObject(item, "starter group");
    return { system: readOneOf(group, "system", SYSTEM_GROUPS), grants: decodeGrants(group, "grants") };
  });
}

/** A person as the access routes name them (`AccessPersonBody`). */
export function decodeAccessPerson(person: JsonObject): AccessPerson {
  return {
    userId: readString(person, "user_id"),
    displayName: readString(person, "display_name"),
    email: readString(person, "email"),
    active: readBoolean(person, "active"),
  };
}

export function decodeSuggestions(body: unknown): readonly AccountSuggestion[] {
  return readArray(body, "suggestions", (item) => {
    const suggestion = readObject(item, "suggestion");
    return { userId: readString(suggestion, "user_id"), displayName: readString(suggestion, "display_name"), emailHint: readString(suggestion, "email_hint") };
  });
}
